"use strict";
/**
 * A generic toggle that controls another element's visibility
 *
 * sample usage:
 *
 * <button class="js-toggle" data-toggle-target="my-element">
 *  <span class="js-toggle__toggle">Show more</span>
 *  <span class="js-toggle__is-toggled hidden">Show less</span>
 * </button>
 *
 * <div id="my-element" class="hidden">I am initially hidden</div>
 *
 * If you want to toggle only once and hide the button afterwards, use:
 *
 * <button class="js-toggle" data-toggle-target="my-element" data-toggle-hide-self>Toggle</button>
 * <div id="my-element" class="hidden">I am initially hidden</div>
 */
Object.defineProperty(exports, "__esModule", { value: true });
const toggleElement = {
    /**
     *
     * @param toggleEl
     */
    initToggle: function (toggleEl) {
        const self = this;
        const target = toggleEl.dataset.toggleTarget ? document.getElementById(toggleEl.dataset.toggleTarget) : null;
        if (target) {
            // Set aria-attributes for accessibility
            toggleEl.setAttribute('aria-controls', toggleEl.dataset.toggleTarget);
            if (toggleEl.classList.contains('is-toggled') && toggleEl.getAttribute('aria-expanded') !== 'true')
                toggleEl.setAttribute('aria-expanded', 'true');
            toggleEl.addEventListener('click', function (e) {
                e.preventDefault();
                self.toggle(toggleEl);
            });
        }
    },
    /**
     *  Toggle function. Give the toggler as parameter.
     *
     * @param toggleEl element with js-toggle-class, for example button
     * @param isOpen optional boolean to force open or close
     */
    toggle: function (toggleEl, isOpen) {
        isOpen = typeof isOpen === 'boolean' ? isOpen : toggleEl.getAttribute('aria-expanded') !== 'true' || !toggleEl.classList.contains('is-toggled');
        const target = toggleEl.dataset.toggleTarget ? document.getElementById(toggleEl.dataset.toggleTarget) : null;
        if (target) {
            if (!isOpen) {
                target.classList.add('hidden');
                toggleEl.classList.remove('is-toggled');
                toggleEl.setAttribute('aria-expanded', 'false');
            }
            else {
                target.classList.remove('hidden');
                toggleEl.classList.add('is-toggled');
                toggleEl.setAttribute('aria-expanded', 'true');
            }
            // Hide toggle button if data-toggle-hide-self is set
            if (typeof toggleEl.dataset.toggleHideSelf !== 'undefined') {
                toggleEl.classList.add('hidden');
            }
            // Show/hide content in toggle button based on current state
            const toggleContent = toggleEl.querySelectorAll('.js-toggle__toggle');
            const isToggledContent = toggleEl.querySelectorAll('.js-toggle__is-toggled');
            if (isOpen) {
                toggleContent.forEach(el => el.classList.add('hidden'));
                isToggledContent.forEach(el => el.classList.remove('hidden'));
            }
            else {
                toggleContent.forEach(el => el.classList.remove('hidden'));
                isToggledContent.forEach(el => el.classList.add('hidden'));
            }
            // Multiple elements might target same toggle target, set correct state for all
            const alternateToggles = document.querySelectorAll('.js-toggle[data-toggle-target="' + toggleEl.dataset.toggleTarget + '"]');
            for (const toggleElement of alternateToggles) {
                if (toggleElement !== toggleEl) {
                    if (!isOpen) {
                        toggleElement.classList.remove('is-toggled');
                        toggleElement.setAttribute('aria-expanded', 'false');
                    }
                    else {
                        toggleElement.classList.add('is-toggled');
                        toggleElement.setAttribute('aria-expanded', 'true');
                    }
                }
            }
        }
        // Functionality to toggle other js-toggles inside same parentElement
        if (toggleEl.dataset.toggleSiblings && toggleEl.parentElement) {
            const siblings = toggleEl.parentElement?.querySelectorAll('.js-toggle');
            siblings.forEach((sibling) => {
                if (sibling !== toggleEl) {
                    sibling.classList.remove('is-toggled');
                    sibling.setAttribute('aria-expanded', 'false');
                    const targetId = sibling.dataset.toggleTarget;
                    if (targetId) {
                        const target = document.getElementById(targetId);
                        target?.classList.add('hidden');
                    }
                }
            });
        }
    },
};
exports.default = toggleElement;
